// =====================================================================
// Estoque (S64) — MÓDULO PRÓPRIO do CRM (rota /estoque). Reusa o backend
// F2-ext + Fase 2 (lotes/FEFO + fornecedores). Abas:
//   • Produtos — dashboard de alertas + lista + movimentar + kardex
//   • Lotes & Validade — saldo líquido por lote (FEFO) + badge de vencimento
//   • Fornecedores — CRUD
// Multi-tenant: backend filtra por companyId do JWT. MUI v4.
// =====================================================================
import React, { useState, useEffect, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Button, CircularProgress, Typography, Table, TableHead, TableBody,
  TableRow, TableCell, Box, Chip, TextField, Tooltip, IconButton, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import WarningIcon from "@material-ui/icons/Warning";
import EditIcon from "@material-ui/icons/Edit";
import HistoryIcon from "@material-ui/icons/History";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { toast } from "react-toastify";

import {
  listStockProducts,
  getStockProduct,
  stockAlerts,
  listStockLots,
  listStockSuppliers,
  financeErrorMessage,
  fmtMoney,
  listStockKits,
  deleteStockKit,
  applyStockKit
} from "../../services/financeService";
import StockProductDialog from "../FinanceiroClinica/StockProductDialog";
import StockMovementDialog from "../FinanceiroClinica/StockMovementDialog";
import SupplierDialog from "./SupplierDialog";
import KitDialog from "./KitDialog";
import { AuthContext } from "../../context/Auth/AuthContext";
import useClinicUnits from "../../hooks/useClinicUnits";
import UnitSelector from "../../components/UnitSelector";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(2), overflowY: "auto", ...theme.scrollbarStyles },
  dash: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: theme.spacing(1) },
  dashCard: { padding: theme.spacing(1.5), minWidth: 150, flex: "1 1 140px", borderRadius: 8 },
  toolbar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: theme.spacing(1) },
  loadingWrap: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
  emptyState: { padding: theme.spacing(4), textAlign: "center", color: theme.palette.text.secondary },
  chipsWrap: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4, marginBottom: 4 },
  tabs: { marginBottom: theme.spacing(2) }
}));

const fmtDateBR = (s) => {
  if (!s) return "—";
  const d = String(s).slice(0, 10).split("-");
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : s;
};
const TYPE_LABEL = { entrada: "Entrada", saida: "Saída", ajuste: "Ajuste" };

// dias até a validade (negativo = vencido); null se sem validade
const daysToExpiry = (s) => {
  if (!s) return null;
  const d = new Date(`${String(s).slice(0, 10)}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
};
const expiryColor = (days) => {
  if (days === null) return undefined;
  if (days < 0) return "#757575";   // vencido (cinza)
  if (days <= 30) return "#c62828"; // <=30d (vermelho)
  if (days <= 60) return "#b26a00"; // <=60d (âmbar)
  return "#2e7d32";                 // ok (verde)
};

// ---- Kardex (histórico de movimentos de um produto) ----
const KardexDialog = ({ open, productId, onClose }) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { product, movements }
  useEffect(() => {
    let alive = true;
    if (open && productId) {
      setLoading(true);
      getStockProduct(productId)
        .then((d) => { if (alive) setData(d); })
        .catch((e) => toast.error(financeErrorMessage(e)))
        .finally(() => { if (alive) setLoading(false); });
    } else {
      setData(null);
    }
    return () => { alive = false; };
  }, [open, productId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Histórico — {(data && data.product && data.product.name) || "produto"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : !data || !data.movements || data.movements.length === 0 ? (
          <Typography color="textSecondary">Sem movimentos registrados.</Typography>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ minWidth: 640 }}>
            <TableHead><TableRow>
              <TableCell>Data</TableCell><TableCell>Tipo</TableCell>
              <TableCell align="right">Qtd</TableCell><TableCell>Lote</TableCell>
              <TableCell>Validade</TableCell><TableCell>Motivo</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.movements.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{fmtDateBR(m.createdAt)}</TableCell>
                  <TableCell>{TYPE_LABEL[m.type] || m.type}</TableCell>
                  <TableCell align="right">{Number(m.qty)}</TableCell>
                  <TableCell>{m.lot || "—"}</TableCell>
                  <TableCell>{fmtDateBR(m.expiry)}</TableCell>
                  <TableCell>{m.reason || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

const Estoque = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { user } = useContext(AuthContext);
  const isAdmin = !!(user && (user.profile === "admin" || user.super));
  // [S90] Unidades do Pablo (modelo B) — seletor de unidade (Moema/Itajaí/…).
  const { units, loading: unitsLoading, hasUnits, activeUnit, setActiveUnit } =
    useClinicUnits(user && user.companyId, isAdmin);
  const [subtab, setSubtab] = useState(0);
  // Produtos
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [alerts, setAlerts] = useState({ lowStock: [], expiring: [], stockValue: 0, controlledCount: 0 });
  const [productOpen, setProductOpen] = useState(false);
  const [productEdit, setProductEdit] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [kardexId, setKardexId] = useState(null);
  // Lotes
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  // Fornecedores
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierEdit, setSupplierEdit] = useState(null);
  // Kits
  const [kits, setKits] = useState([]);
  const [kitsLoading, setKitsLoading] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const [kitEdit, setKitEdit] = useState(null);
  const [applyTarget, setApplyTarget] = useState(null);
  const [applyQty, setApplyQty] = useState("1");
  const [applying, setApplying] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, al] = await Promise.all([
        listStockProducts({ search: search || undefined, unitId: activeUnit, pageSize: 200 }),
        stockAlerts(activeUnit).catch(() => ({ lowStock: [], expiring: [] }))
      ]);
      setProducts((prods && prods.products) || prods || []);
      setAlerts({
        lowStock: (al && al.lowStock) || [],
        expiring: (al && al.expiring) || [],
        stockValue: (al && al.stockValue) || 0,
        controlledCount: (al && al.controlledCount) || 0
      });
    } catch (e) {
      toast.error(financeErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [search, activeUnit]);

  const fetchLots = useCallback(async () => {
    setLotsLoading(true);
    try {
      const r = await listStockLots();
      setLots((r && r.lots) || []);
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setLotsLoading(false); }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    setSuppliersLoading(true);
    try {
      const r = await listStockSuppliers();
      setSuppliers((r && r.suppliers) || []);
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setSuppliersLoading(false); }
  }, []);

  const fetchKits = useCallback(async () => {
    setKitsLoading(true);
    try {
      const r = await listStockKits();
      setKits((r && r.kits) || []);
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setKitsLoading(false); }
  }, []);

  const handleDeleteKit = async (k) => {
    try { await deleteStockKit(k.id); toast.success("Kit removido."); fetchKits(); }
    catch (e) { toast.error(financeErrorMessage(e)); }
  };

  const handleApplyKit = async () => {
    if (!applyTarget) return;
    setApplying(true);
    try {
      const res = await applyStockKit(applyTarget.id, { quantity: Math.max(1, Number(applyQty) || 1) });
      toast.success(`Kit aplicado — ${(res && res.movements && res.movements.length) || 0} insumo(s) baixado(s).`);
      setApplyTarget(null);
      fetchAll();
      if (subtab === 3) fetchKits();
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setApplying(false); }
  };

  // [S90] só busca depois de saber a situação de unidade (evita fetch sem unitId
  // numa company multi-unidade → 403 pro não-admin). Re-busca ao trocar de unidade.
  useEffect(() => {
    if (unitsLoading) return;
    if (hasUnits && activeUnit == null) return;
    fetchAll();
  }, [unitsLoading, hasUnits, activeUnit]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (subtab === 1) fetchLots();
    if (subtab === 2) fetchSuppliers();
    if (subtab === 3) fetchKits();
  }, [subtab, fetchLots, fetchSuppliers, fetchKits]);

  const lowCount = alerts.lowStock.length;
  const expCount = alerts.expiring.length;
  const allOk = lowCount === 0 && expCount === 0;
  const prodName = (id) => {
    const p = products.find((x) => Number(x.id) === Number(id));
    return p ? p.name : `Produto #${id}`;
  };

  const headerButton = () => {
    if (subtab === 0) {
      return (
        <Button variant="contained" color="primary" startIcon={<AddIcon />}
          onClick={() => { setProductEdit(null); setProductOpen(true); }}>Novo Produto</Button>
      );
    }
    if (subtab === 2) {
      return (
        <Button variant="contained" color="primary" startIcon={<AddIcon />}
          onClick={() => { setSupplierEdit(null); setSupplierOpen(true); }}>Novo Fornecedor</Button>
      );
    }
    if (subtab === 3) {
      return (
        <Button variant="contained" color="primary" startIcon={<AddIcon />}
          onClick={() => { setKitEdit(null); setKitOpen(true); }}>Novo Kit</Button>
      );
    }
    return <span />;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Estoque</Title>
        <MainHeaderButtonsWrapper>{headerButton()}</MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {/* [S90] seletor de unidade (só aparece p/ companies com clinic_units) */}
        <UnitSelector units={units} value={activeUnit} onChange={setActiveUnit} isAdmin={isAdmin} />
        <Tabs className={classes.tabs} value={subtab} onChange={(e, v) => setSubtab(v)}
          indicatorColor="primary" textColor="primary">
          <Tab label="Produtos" />
          <Tab label="Lotes & Validade" />
          <Tab label="Fornecedores" />
          <Tab label="Kits" />
        </Tabs>

        {/* ---------------- PRODUTOS ---------------- */}
        {subtab === 0 && (
          <>
            <div className={classes.dash}>
              <Paper className={classes.dashCard} variant="outlined" style={{ borderColor: lowCount ? "#c62828" : undefined }}>
                <Typography variant="caption" color="textSecondary">Abaixo do mínimo</Typography>
                <Typography variant="h5" style={{ color: lowCount ? "#c62828" : undefined }}>{lowCount}</Typography>
              </Paper>
              <Paper className={classes.dashCard} variant="outlined" style={{ borderColor: expCount ? "#b26a00" : undefined }}>
                <Typography variant="caption" color="textSecondary">Vencendo / vencido</Typography>
                <Typography variant="h5" style={{ color: expCount ? "#b26a00" : undefined }}>{expCount}</Typography>
              </Paper>
              <Paper className={classes.dashCard} variant="outlined">
                <Typography variant="caption" color="textSecondary">Valor do estoque</Typography>
                <Typography variant="h5">{fmtMoney(alerts.stockValue || 0)}</Typography>
              </Paper>
              {alerts.controlledCount > 0 && (
                <Paper className={classes.dashCard} variant="outlined">
                  <Typography variant="caption" color="textSecondary">Controlados (ANVISA)</Typography>
                  <Typography variant="h5">{alerts.controlledCount}</Typography>
                </Paper>
              )}
              {allOk && !loading && (
                <Paper className={classes.dashCard} variant="outlined">
                  <Box display="flex" alignItems="center" height="100%" style={{ gap: 6 }}>
                    <CheckCircleIcon style={{ color: "#2e7d32" }} />
                    <Typography variant="body2">Estoque sob controle</Typography>
                  </Box>
                </Paper>
              )}
            </div>
            {(lowCount > 0 || expCount > 0) && (
              <div className={classes.chipsWrap}>
                {alerts.lowStock.map((a) => (
                  <Chip key={`low-${a.id}`} size="small" icon={<WarningIcon style={{ color: "#fff" }} />}
                    label={`${a.name}: ${Number(a.currentQty)} ${a.unit || ""} (mín ${Number(a.minQty)})`}
                    style={{ backgroundColor: "#c62828", color: "#fff" }} />
                ))}
                {alerts.expiring.map((a, i) => (
                  <Chip key={`exp-${i}`} size="small" icon={<WarningIcon style={{ color: "#fff" }} />}
                    label={`${a.name} • lote ${a.lot || "—"} • ${a.expired ? "VENCIDO" : "vence"} ${fmtDateBR(a.expiry)}`}
                    style={{ backgroundColor: a.expired ? "#757575" : "#b26a00", color: "#fff" }} />
                ))}
              </div>
            )}
            <div className={classes.toolbar} style={{ marginTop: 12 }}>
              <TextField size="small" variant="outlined" label="Buscar produto" value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchAll(); }} style={{ flexGrow: 1, minWidth: 130 }} />
              <Button size="small" onClick={() => fetchAll()}>Buscar</Button>
              <Box flexGrow={1} />
              <Typography variant="body2" color="textSecondary">{products.length} produto(s)</Typography>
            </div>
            {loading ? (
              <div className={classes.loadingWrap}><CircularProgress /></div>
            ) : products.length === 0 ? (
              <div className={classes.emptyState}><Typography>Nenhum produto. Clique em "Novo Produto".</Typography></div>
            ) : (
              <div style={{ overflowX: "auto", width: "100%" }}>
              <Table size="small" style={{ minWidth: 640 }}>
                <TableHead><TableRow>
                  <TableCell>Produto</TableCell><TableCell>Categoria</TableCell>
                  <TableCell align="right">Saldo</TableCell><TableCell align="right">Mínimo</TableCell>
                  <TableCell align="center" style={{ width: 150 }}>Ações</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {products.map((p) => {
                    const low = Number(p.minQty) > 0 && Number(p.currentQty) <= Number(p.minQty);
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>
                      {p.name}
                      {p.refrigerated ? <Tooltip title="Refrigerado (cadeia fria)"><span style={{ marginLeft: 4 }}>❄️</span></Tooltip> : null}
                      {p.controlled ? <Tooltip title="Controlado (ANVISA)"><span style={{ marginLeft: 2 }}>💊</span></Tooltip> : null}
                      {!p.active && <Chip size="small" label="inativo" style={{ marginLeft: 6 }} />}
                    </TableCell>
                        <TableCell>{p.category || "—"}</TableCell>
                        <TableCell align="right" style={{ color: low ? "#c62828" : undefined, fontWeight: low ? 700 : 400 }}>
                          {Number(p.currentQty)} {p.unit || ""}
                        </TableCell>
                        <TableCell align="right">{Number(p.minQty) ? Number(p.minQty) : "—"}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Movimentar (entrada/saída/ajuste)"><IconButton size="small" color="primary" onClick={() => setMoveTarget(p)}><SwapHorizIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Histórico (kardex)"><IconButton size="small" onClick={() => setKardexId(p.id)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => { setProductEdit(p); setProductOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            )}
          </>
        )}

        {/* ---------------- LOTES & VALIDADE ---------------- */}
        {subtab === 1 && (
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
            Os lotes refletem entradas com lote informado. O saldo TOTAL de cada produto (inclusive estoque sem lote) está na aba <b>Produtos</b>.
          </Typography>
        )}
        {subtab === 1 && (
          lotsLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
          : lots.length === 0 ? <div className={classes.emptyState}><Typography>Nenhum lote registrado. Os lotes aparecem ao dar ENTRADA informando o lote/validade.</Typography></div>
          : (
            <div style={{ overflowX: "auto", width: "100%" }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Produto</TableCell><TableCell>Lote</TableCell>
                <TableCell>Validade</TableCell><TableCell align="right">Saldo</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {lots.map((l) => {
                  const days = daysToExpiry(l.expiry);
                  const col = expiryColor(days);
                  const zero = Number(l.qty) <= 0;
                  return (
                    <TableRow key={l.id} hover style={{ opacity: zero ? 0.5 : 1 }}>
                      <TableCell>{prodName(l.productId)}</TableCell>
                      <TableCell>{l.lot}</TableCell>
                      <TableCell style={{ color: col, fontWeight: days !== null && days <= 30 ? 700 : 400 }}>
                        {fmtDateBR(l.expiry)}
                        {days !== null && days < 0 && " (vencido)"}
                        {days !== null && days >= 0 && days <= 60 && ` (${days}d)`}
                      </TableCell>
                      <TableCell align="right">{Number(l.qty)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )
        )}

        {/* ---------------- FORNECEDORES ---------------- */}
        {subtab === 2 && (
          suppliersLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
          : suppliers.length === 0 ? <div className={classes.emptyState}><Typography>Nenhum fornecedor. Clique em "Novo Fornecedor".</Typography></div>
          : (
            <div style={{ overflowX: "auto", width: "100%" }}>
            <Table size="small" style={{ minWidth: 640 }}>
              <TableHead><TableRow>
                <TableCell>Nome</TableCell><TableCell>CNPJ</TableCell>
                <TableCell>Telefone</TableCell><TableCell>E-mail</TableCell>
                <TableCell align="center" style={{ width: 80 }}>Ações</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.name}{!s.active && <Chip size="small" label="inativo" style={{ marginLeft: 6 }} />}</TableCell>
                    <TableCell>{s.cnpj || "—"}</TableCell>
                    <TableCell>{s.phone || "—"}</TableCell>
                    <TableCell>{s.email || "—"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => { setSupplierEdit(s); setSupplierOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )
        )}

        {/* ---------------- KITS ---------------- */}
        {subtab === 3 && (
          kitsLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
          : kits.length === 0 ? <div className={classes.emptyState}><Typography>Nenhum kit. Clique em "Novo Kit" pra montar um conjunto de insumos por procedimento (baixa automática + margem).</Typography></div>
          : (
            <div style={{ overflowX: "auto", width: "100%" }}>
            <Table size="small" style={{ minWidth: 640 }}>
              <TableHead><TableRow>
                <TableCell>Kit / Procedimento</TableCell><TableCell align="center">Insumos</TableCell>
                <TableCell align="right">Custo</TableCell><TableCell align="right">Preço</TableCell>
                <TableCell align="right">Margem</TableCell>
                <TableCell align="center" style={{ width: 130 }}>Ações</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {kits.map((k) => (
                  <TableRow key={k.id} hover style={{ opacity: k.active === false ? 0.5 : 1 }}>
                    <TableCell>{k.name}{k.active === false && <Chip size="small" label="inativo" style={{ marginLeft: 6 }} />}</TableCell>
                    <TableCell align="center">{(k.items || []).length}</TableCell>
                    <TableCell align="right">{fmtMoney(k.cost || 0)}</TableCell>
                    <TableCell align="right">{k.price != null ? fmtMoney(k.price) : "—"}</TableCell>
                    <TableCell align="right" style={{ color: k.margin == null ? undefined : k.margin >= 0 ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                      {k.margin != null ? fmtMoney(k.margin) : "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Aplicar (baixa os insumos no estoque)"><span><IconButton size="small" color="primary" disabled={k.active === false} onClick={() => { setApplyTarget(k); setApplyQty("1"); }}><PlaylistAddCheckIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => { setKitEdit(k); setKitOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDeleteKit(k)}><DeleteOutlineIcon fontSize="small" style={{ color: "#d32f2f" }} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )
        )}
      </Paper>

      <StockProductDialog open={productOpen} product={productEdit}
        unitId={hasUnits ? (activeUnit === "all" ? undefined : activeUnit) : undefined}
        onClose={() => setProductOpen(false)}
        onSaved={() => { setProductOpen(false); fetchAll(); }} />
      <StockMovementDialog open={!!moveTarget} product={moveTarget}
        onClose={() => setMoveTarget(null)}
        onSaved={() => { setMoveTarget(null); fetchAll(); if (subtab === 1) fetchLots(); }} />
      <KardexDialog open={!!kardexId} productId={kardexId} onClose={() => setKardexId(null)} />
      <SupplierDialog open={supplierOpen} supplier={supplierEdit}
        onClose={() => setSupplierOpen(false)}
        onSaved={() => { setSupplierOpen(false); fetchSuppliers(); }} />
      <KitDialog open={kitOpen} kit={kitEdit} products={products}
        onClose={() => setKitOpen(false)}
        onSaved={() => { setKitOpen(false); fetchKits(); }} />
      <Dialog open={!!applyTarget} onClose={() => (!applying ? setApplyTarget(null) : null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Aplicar kit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Aplicar <b>{applyTarget?.name}</b> vai dar baixa dos insumos no estoque (FEFO).
          </Typography>
          <TextField label="Quantas vezes" type="number" value={applyQty}
            onChange={(e) => setApplyQty(e.target.value)} fullWidth variant="outlined" size="small"
            margin="dense" inputProps={{ step: "1", min: "1" }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyTarget(null)} disabled={applying}>Cancelar</Button>
          <Button onClick={handleApplyKit} color="primary" variant="contained" disabled={applying}
            startIcon={applying ? <CircularProgress size={16} color="inherit" /> : null}>
            {applying ? "Aplicando..." : "Confirmar baixa"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Estoque;
